/**
 * Region Recommendation Popup
 * -----------------------------------------------------------------------
 * India store build — shows a popup to US visitors recommending the US store.
 * Identical architecture to the US store build; only REGION_CONFIG differs.
 *
 * Files:
 *   assets/region-popup.js      ← this file
 *   assets/region-popup.css
 *   snippets/region-popup.liquid
 * -----------------------------------------------------------------------
 */

/* =========================================================================
   CONFIGURATION  — edit this object to adjust behaviour
   ========================================================================= */
const REGION_CONFIG = {
  /** URL of the US store. Used for the primary CTA button. */
  usStore: 'https://kshetrafoods.us/',

  /** Milliseconds to wait after DOMContentLoaded before showing the popup. */
  popupDelay: 1000,

  /** Days to suppress the popup when the visitor checks "Remember my choice". */
  rememberDays: 30,

  /** Hours to cache the detected country in localStorage (reduces API calls). */
  countryCacheHours: 24,

  /**
   * Primary GeoIP endpoint.
   * Response must include a top-level `country_code` or `country` field.
   */
  geoApi: 'https://ipwho.is/',

  /**
   * Fallback GeoIP endpoint.
   * ipapi returns plain text — just the 2-letter country code.
   */
  geoApiFallback: 'https://ipapi.co/country/',

  /** ISO-3166-1 alpha-2 code for the target country. */
  targetCountry: 'US',

  /** localStorage / sessionStorage key prefix. */
  storagePrefix: 'kshetra_region_',
};

/* =========================================================================
   STORAGE HELPERS
   ========================================================================= */

const Storage = {
  /**
   * Persist a value with an optional TTL (in hours).
   * Falls back silently if localStorage is unavailable.
   */
  set(key, value, ttlHours = null) {
    try {
      const payload = {
        value,
        expires: ttlHours ? Date.now() + ttlHours * 3600 * 1000 : null,
      };
      localStorage.setItem(REGION_CONFIG.storagePrefix + key, JSON.stringify(payload));
    } catch (_) {}
  },

  /** Retrieve a value; returns null if missing or expired. */
  get(key) {
    try {
      const raw = localStorage.getItem(REGION_CONFIG.storagePrefix + key);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (payload.expires && Date.now() > payload.expires) {
        localStorage.removeItem(REGION_CONFIG.storagePrefix + key);
        return null;
      }
      return payload.value;
    } catch (_) {
      return null;
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(REGION_CONFIG.storagePrefix + key);
    } catch (_) {}
  },
};

/* =========================================================================
   SESSION HELPER
   ========================================================================= */

const Session = {
  set(key, value) {
    try {
      sessionStorage.setItem(REGION_CONFIG.storagePrefix + key, JSON.stringify(value));
    } catch (_) {}
  },
  get(key) {
    try {
      const raw = sessionStorage.getItem(REGION_CONFIG.storagePrefix + key);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  },
};

/* =========================================================================
   COUNTRY DETECTION
   ========================================================================= */

/**
 * Detect the visitor's country.
 *
 * Priority:
 *   1. Cloudflare CF-IPCountry header (via <meta> tag if injected by edge).
 *   2. Cached value from a previous detection (localStorage, 24 h TTL).
 *   3. ipwho.is primary API.
 *   4. ipapi.co/country/ fallback API.
 *
 * Returns the 2-letter ISO country code or null on failure.
 */
async function detectCountry() {
  // 1. Cloudflare header (exposed via a <meta> tag in the Liquid snippet)
  const cfMeta = document.querySelector('meta[name="cf-ipcountry"]');
  if (cfMeta && cfMeta.content && cfMeta.content.length === 2) {
    const code = cfMeta.content.toUpperCase();
    if (code !== 'XX') {
      Storage.set('country', code, REGION_CONFIG.countryCacheHours);
      return code;
    }
  }

  // 2. Cached result
  const cached = Storage.get('country');
  if (cached) return cached;

  // 3. Primary GeoIP: ipwho.is
  try {
    const res = await fetch(REGION_CONFIG.geoApi, {
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) {
      const data = await res.json();
      const code = (data.country_code || data.country || '').toUpperCase();
      if (code.length === 2) {
        Storage.set('country', code, REGION_CONFIG.countryCacheHours);
        return code;
      }
    }
  } catch (err) {
    if (process?.env?.NODE_ENV === 'development') {
      console.warn('[RegionPopup] Primary GeoIP failed:', err);
    }
  }

  // 4. Fallback GeoIP: ipapi.co (plain text)
  try {
    const res = await fetch(REGION_CONFIG.geoApiFallback, {
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) {
      const code = (await res.text()).trim().toUpperCase();
      if (code.length === 2) {
        Storage.set('country', code, REGION_CONFIG.countryCacheHours);
        return code;
      }
    }
  } catch (err) {
    if (process?.env?.NODE_ENV === 'development') {
      console.warn('[RegionPopup] Fallback GeoIP failed:', err);
    }
  }

  return null;
}

/* =========================================================================
   POPUP CONTROLLER
   ========================================================================= */

const RegionPopup = {
  overlay: null,
  dialog: null,
  rememberCheckbox: null,
  previouslyFocused: null,

  init() {
    this.overlay = document.getElementById('region-popup-overlay');
    this.dialog  = document.getElementById('region-popup');

    if (!this.overlay || !this.dialog) return;

    this.rememberCheckbox = document.getElementById('region-popup-remember');

    const btnUS    = document.getElementById('region-popup-visit-us');
    const btnStay  = document.getElementById('region-popup-stay-india');
    const btnClose = document.getElementById('region-popup-close');

    btnUS?.addEventListener('click',    () => this.visitUS());
    btnStay?.addEventListener('click',  () => this.stayInIndia());
    btnClose?.addEventListener('click', () => this.stayInIndia());

    // Backdrop click closes the popup (stay in India)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.stayInIndia();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('is-visible')) {
        this.stayInIndia();
      }
    });

    // Focus trap
    this.dialog.addEventListener('keydown', (e) => this._trapFocus(e));
  },

  show() {
    if (!this.overlay) return;

    setTimeout(() => {
      this.previouslyFocused = document.activeElement;
      this.overlay.classList.add('is-visible');
      this.dialog.removeAttribute('aria-hidden');
      this.overlay.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';

      const firstFocusable = this._getFocusable()[0];
      firstFocusable?.focus();
    }, REGION_CONFIG.popupDelay);
  },

  /** Redirect to US store. */
  visitUS() {
    this._saveChoice();
    this.close();
    window.location.href = REGION_CONFIG.usStore;
  },

  /** Close and stay on India store. */
  stayInIndia() {
    this._saveChoice();
    this.close();
  },

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-visible');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.dialog.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.previouslyFocused?.focus();
  },

  _saveChoice() {
    if (this.rememberCheckbox?.checked) {
      Storage.set('dismissed', true, REGION_CONFIG.rememberDays * 24);
    } else {
      Session.set('dismissed', true);
    }
  },

  _isDismissed() {
    return Storage.get('dismissed') === true || Session.get('dismissed') === true;
  },

  _trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = this._getFocusable();
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  },

  _getFocusable() {
    return Array.from(
      this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  },
};

/* =========================================================================
   ENTRY POINT
   ========================================================================= */

async function initRegionPopup() {
  RegionPopup.init();

  if (RegionPopup._isDismissed()) return;

  const country = await detectCountry();
  if (!country) return;

  if (country === REGION_CONFIG.targetCountry) {
    RegionPopup.show();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRegionPopup);
} else {
  initRegionPopup();
}
